/* ============================================================
   Dathent — Edit Post screen
   Form view opened from Library / Calendar / Post Preview.
   4 tabs (Copy / Media / Schedule / Advanced) + sticky live mini-preview.
   Source: design pack pages/edit-post.jsx.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { isVideoAsset, resolvePosterUrl, formatDuration } from '../components/preview/normalizePost.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons, BrandTints } from '../components/pack-icons/brands';
import { getCharacterLimit } from '../components/preview/platformText.js';
import { useActivity } from '../components/AppShell';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { startPublishNowOperation } from '../lib/operations.js';
import { notify, friendlyErrorMessage } from '../lib/notifications.js';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { DeviceTimezoneLabel } from '../components/DeviceTimezoneLabel.jsx';
import { LibraryPicker } from './CreatePost.jsx';
import { useUploadProgress } from '../components/UploadProgress.jsx';
import { GenerationLevelPicker, useGenerationLevel } from '../components/GenerationLevelPicker.jsx';
import { generationLevelPayloadFields } from '../lib/generationLevel.js';
import { generationLevelPickerEnabled } from '../lib/flags.js';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { deriveImageAllowance } from '../lib/imageAllowance.js';
import { deriveTextAllowance } from '../lib/textAllowance.js';
import CreationAccessNotice, { creationAccessState } from '../components/billing/CreationAccessNotice.jsx';
import { useUpgrade } from '../contexts/UpgradeContext.jsx';
import { canSaveAsDraft } from '../lib/editPostSave.js';
import { publishedAccountIds, initialPostAccounts, scheduleTimeError, requiresNewSchedule } from '../lib/editPostPublishing.js';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function newTargetIdFromClone(result, accountId) {
  const body = result?.data || result;
  const targets = body?.targets || body?.post?.targets || [];
  return body?.recovery?.successorTargetId
    || targets.find((target) => target.socialAccountId === accountId)?.id || null;
}

// Display list (id + name) for the account picker / platform hint. Character
// limits are NOT duplicated here — they come from the single source of truth in
// components/preview/platformText.js (getCharacterLimit), so adding/adjusting a
// limit happens in exactly one place.
const PLATFORM_DEFS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'telegram', name: 'Telegram' },
  { id: 'threads', name: 'Threads' },
];

// Native language labels for the publish-language mismatch notice on
// the Schedule tab. Keeping it inline here (and in Misc.jsx + the
// regen panel earlier in this file) is intentional: the list is
// short, almost never changes, and avoids a cross-screen import.
const LANGUAGE_LABELS = {
  en: 'English',
  uk: 'Українська',
  ru: 'Русский',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pl: 'Polski',
  cs: 'Čeština',
  pt: 'Português',
  nl: 'Nederlands',
  sv: 'Svenska',
  lt: 'Lietuvių',
  lv: 'Latviešu',
};
function languageLabel(code) {
  const key = (code || '').toLowerCase();
  return LANGUAGE_LABELS[key] || (code ? String(code).toUpperCase() : '');
}

function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

function fmtScheduled(s) {
  if (!s) return 'later';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return 'later';
  return d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Format a Date or ISO string for <input type="datetime-local"> (YYYY-MM-DDTHH:mm in local time)
function toLocalDatetimeInput(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  const msg = err?.body?.error?.message || err?.body?.message || err?.data?.message || err?.message || '';
  const status = err?.status || err?.body?.error?.status;
  // Compose "<HTTP status> <message>" so we don't surface a generic
  // "Unknown error" when the API returns a useful error body.
  if (status && msg) return `${status}: ${msg}`;
  if (msg) return msg;
  return 'Unknown error';
}

/* ---------------- Field wrapper ---------------- */
function EpField({ label, hint, error, count, max, children }) {
  return (
    <div className={`ep-field ${error ? 'is-error' : ''}`}>
      <div className="ep-field-h">
        <label className="ep-field-l">{label}</label>
        {(count != null && max != null) && (
          <span className={`ep-field-count mono ${count > max ? 'is-over' : count > max * 0.85 ? 'is-warn' : ''}`}>
            {count.toLocaleString()} / {max.toLocaleString()}
          </span>
        )}
      </div>
      {children}
      {error && <div className="ep-field-err"><I.alert size={11} /> {error}</div>}
      {hint && !error && <div className="ep-field-hint">{hint}</div>}
    </div>
  );
}

// Reel (video) fields for a mapped carousel slide — spread onto every slide the
// render consumes (SlideThumb / MiniPreview / SlideLightbox branch on these). All
// null/false for image slides, so the video branches stay inert (byte-identical).
// A reel slide sets imageUrl:null (never shove the .mp4 into imageUrl) — callers
// compute imageUrl separately with videoImageUrl().
function slideVideoFields(mediaAsset) {
  const vid = isVideoAsset(mediaAsset);
  return {
    isVideo: vid,
    // isReel is resolved here (portrait + <=90s) so the render reads only the
    // boolean — width/height are not carried onto the slide (no live reader).
    isReel: vid && (mediaAsset?.height || 0) > (mediaAsset?.width || 0) && (mediaAsset?.durationSeconds ?? 0) <= 90,
    publicUrl: vid ? (mediaAsset?.publicUrl || null) : null,
    posterUrl: vid ? resolvePosterUrl(mediaAsset) : null,
    durationSeconds: vid ? (mediaAsset?.durationSeconds ?? null) : null,
  };
}

/* ---------------- Slide thumb ----------------
 * Now draggable. We use native HTML5 drag-and-drop (no react-dnd dep)
 * so the tile can be picked up by the cover area and dropped on any
 * other slide. dataTransfer carries the source index; onDrop in the
 * receiving thumb calls onReorder(from, to) on the parent. Keyboard
 * arrows (chevLeft/chevRight) stay for a11y + non-mouse users. */
function SlideThumb({
  slide, idx, isActive, onClick, total, onMoveLeft, onMoveRight, onReorder, isDragging,
  onArmDelete, onCancelDelete, onConfirmDelete, isConfirmingDelete, isDeleting, canDelete, isRegenerating
}) {
  const { t } = useTranslation();
  const isVid = !!slide.isVideo;
  const tint = isVid
    ? (slide.posterUrl ? { backgroundImage: `url("${slide.posterUrl}")` } : {})
    : slide.imageUrl
      ? { backgroundImage: `url("${slide.imageUrl}")` }
      : { background: `linear-gradient(135deg, ${slide.grad?.[0] || '#888'}, ${slide.grad?.[1] || '#444'})` };
  return (
    <div
      className={`ep-slide ${isActive ? 'is-active' : ''} ${isDragging ? 'is-dragging' : ''} ${isRegenerating ? 'is-regenerating' : ''} ${isVid ? 'is-video' : ''}`}
      onClick={onClick}
      draggable={!isConfirmingDelete}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', String(idx));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const from = Number(e.dataTransfer.getData('text/plain'));
        if (Number.isFinite(from) && from !== idx && onReorder) onReorder(from, idx);
      }}
      title={isConfirmingDelete ? '' : t('editPost.slideDragHint')}
    >
      <div className="ep-slide-cover" style={{
        ...tint,
        // Images: 'contain' so the FULL image shows (no crop). Reel poster:
        // 'cover' to fill the square thumb. Dark backdrop fills the letterbox.
        backgroundSize: isVid ? 'cover' : 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: (isVid || slide.imageUrl) ? '#0f172a' : undefined
      }}>
        <span className="ep-slide-tag mono">{String(idx + 1).padStart(2, '0')}</span>
        {slide.cap && <span className="ep-slide-cap">{slide.cap.split('\n')[0]}</span>}
        {isVid && (
          <>
            <span className="dv-play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></span>
            <span className="dv-badge dv-badge-kind" style={{ left: 'auto', right: 8 }}>{slide.isReel ? 'REEL' : 'VIDEO'}</span>
            {formatDuration(slide.durationSeconds) && <span className="dv-badge dv-badge-dur">{formatDuration(slide.durationSeconds)}</span>}
          </>
        )}
        {slide.uploading && (
          <div className="ep-slide-uploading" aria-live="polite">
            <span className="ep-slide-uploading-spinner" aria-hidden="true" />
            <span className="ep-slide-uploading-label">{t('editPost.uploading')}</span>
          </div>
        )}
        {isRegenerating && !slide.uploading && (
          <div className="ep-slide-uploading ep-slide-regen" aria-live="polite">
            <span className="ep-slide-uploading-spinner" aria-hidden="true" />
            <span className="ep-slide-uploading-label">{t('editPost.regenerating')}</span>
          </div>
        )}
      </div>
      <div className="ep-slide-actions" onClick={(e) => e.stopPropagation()}>
        {idx > 0 && (
          <button title={t('editPost.moveLeft')} onClick={onMoveLeft} type="button"><I.chevLeft size={11} /></button>
        )}
        {idx < total - 1 && (
          <button title={t('editPost.moveRight')} onClick={onMoveRight} type="button"><I.chevRight size={11} /></button>
        )}
        {/* Delete button — hidden when this is the last remaining slide
            on an image-required post (backend would refuse anyway, but
            hiding avoids a confusing error). Two-step confirm: first
            click arms the inline "Delete?" prompt overlay; second click
            within 4s fires the API. */}
        {canDelete && !isConfirmingDelete && (
          <button
            title={t('editPost.deleteSlide')}
            onClick={(e) => { e.stopPropagation(); onArmDelete(); }}
            type="button"
            style={{ color: 'var(--text-subtle)' }}
          >
            <I.trash size={11} />
          </button>
        )}
      </div>

      {/* Inline confirm overlay — full-tile dark scrim with Cancel +
          Delete buttons. Centered, no modal — UX stays on the same
          place, user doesn't lose context. */}
      {isConfirmingDelete && (
        <div
          className="ep-slide-confirm"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', inset: 0,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            background: 'rgba(15, 23, 42, 0.88)',
            backdropFilter: 'blur(2px)',
            borderRadius: 'var(--r-md)',
            zIndex: 5
          }}
        >
          <div style={{ fontSize: 12, color: '#fff', textAlign: 'center', padding: '0 12px' }}>
            {t('editPost.deleteSlideConfirm', { number: idx + 1 })}
          </div>
          <div style={{ display: 'inline-flex', gap: 6 }}>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={(e) => { e.stopPropagation(); onCancelDelete(); }}
              disabled={isDeleting}
              style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}
            >
              {t('editPost.cancel')}
            </button>
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={(e) => { e.stopPropagation(); onConfirmDelete(); }}
              disabled={isDeleting}
            >
              {isDeleting ? t('editPost.deleting') : t('editPost.delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Live mini preview ---------------- */
function MiniPreview({ platform, caption, hashtags, slide, slideIdx, slideCount, handle, onPrev, onNext, onOpen, isRegenerating }) {
  const { t } = useTranslation();
  const hasMultipleSlides = slideCount > 1;
  // Reel (video) slide → real <video> letterboxed on #0f172a; images unchanged.
  const isVid = !!slide?.isVideo && !!slide?.publicUrl;
  const videoRef = useRef(null);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 640;
  // One video at a time: pause + reset the reel when the previewed slide changes
  // (a mixed image+video post can navigate away from a playing reel).
  useEffect(() => {
    const v = videoRef.current;
    return () => { if (v) { try { v.pause(); v.currentTime = 0; } catch (_) { /* detached */ } } };
  }, [slideIdx]);
  const slideBg = isVid
    ? { backgroundColor: '#0f172a' }
    : slide?.imageUrl
    ? {
        backgroundImage: `url("${slide.imageUrl}")`,
        backgroundSize: 'contain',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#0f172a'
      }
    : { background: `linear-gradient(135deg, ${slide?.grad?.[0] || platform.tint?.from || '#888'}, ${slide?.grad?.[1] || platform.tint?.to || '#444'})` };
  // Click on media opens lightbox. Arrows stopPropagation so they
  // don't also fire onOpen — without that, clicking next/prev would
  // toggle the lightbox on every tap.
  const mediaClickable = Boolean((slide?.imageUrl || isVid) && onOpen);
  const handleMediaClick = mediaClickable ? () => onOpen() : undefined;
  const handleMediaKeyDown = mediaClickable
    ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }
    : undefined;
  return (
    <div className="ep-mp">
      <div className="ep-mp-h">
        <span
          className={`ob-net-ic-${platform.id}`}
          style={{ width: 26, height: 26, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
        >
          {BrandIcons[platform.id]?.({ size: 13 })}
        </span>
        <div>
          <div className="ep-mp-name">{handle || platform.name}</div>
          <div className="ep-mp-sub mono">{platform.name}</div>
        </div>
      </div>
      <div
        className={`ep-mp-media${mediaClickable ? ' ep-mp-media--clickable' : ''}`}
        style={slideBg}
        onClick={handleMediaClick}
        onKeyDown={handleMediaKeyDown}
        role={mediaClickable ? 'button' : undefined}
        tabIndex={mediaClickable ? 0 : undefined}
        aria-label={mediaClickable ? t('editPost.openFullPreview') : undefined}
      >
        {isVid && (
          <video
            ref={videoRef}
            className="dv-player"
            src={slide.publicUrl}
            {...(slide.posterUrl ? { poster: slide.posterUrl } : {})}
            muted
            loop
            playsInline
            preload="metadata"
            autoPlay={!reducedMotion && !isMobile}
          />
        )}
        {isVid ? <div className="dv-scrim" /> : <div className="ep-mp-grad" />}
        {isVid && (reducedMotion || isMobile) && (
          <div className="dv-play" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg></div>
        )}
        {slide?.cap && (
          <div className="ep-mp-cap">
            {slide.cap.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
        {hasMultipleSlides && onPrev && (
          <button
            type="button"
            className="ep-mp-nav ep-mp-nav--prev"
            onClick={(event) => {
              event.stopPropagation();
              onPrev();
            }}
            aria-label={t('editPost.previousSlide')}
            disabled={slideIdx <= 0}
          >
            <I.chevLeft size={14} />
          </button>
        )}
        {hasMultipleSlides && onNext && (
          <button
            type="button"
            className="ep-mp-nav ep-mp-nav--next"
            onClick={(event) => {
              event.stopPropagation();
              onNext();
            }}
            aria-label={t('editPost.nextSlide')}
            disabled={slideIdx >= slideCount - 1}
          >
            <I.chevRight size={14} />
          </button>
        )}
        {hasMultipleSlides && (
          <span className="ep-mp-counter mono">{slideIdx + 1} / {slideCount}</span>
        )}
        {isRegenerating && (
          <div className="ep-mp-regen" aria-live="polite">
            <span className="ep-slide-uploading-spinner" aria-hidden="true" />
            <span className="ep-mp-regen-label">{t('editPost.regenerating')}</span>
          </div>
        )}
      </div>
      <div className="ep-mp-cap-block">
        {handle && <span className="ep-mp-handle">{handle.replace('@', '').replace('/', '')}</span>}{' '}
        {caption.split('\n').slice(0, 3).map((p, i) => (
          <span key={i}>{p}{i < 2 && p && <br />}</span>
        ))}
        {hashtags.length > 0 && (
          <span className="ep-mp-tags"> {hashtags.slice(0, 4).join(' ')}{hashtags.length > 4 && '…'}</span>
        )}
      </div>
    </div>
  );
}

/* ---------------- Fullscreen lightbox ---------------- */
function SlideLightbox({ slides, slideIdx, onClose, onPrev, onNext }) {
  const { t } = useTranslation();
  // Escape closes, arrow keys nav. We attach to window because the
  // lightbox content captures focus when opened but we don't want to
  // rely on a particular focused element to get keyboard nav.
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowLeft') onPrev();
      else if (event.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while the overlay is up — otherwise scrolling
    // the page behind the lightbox is jarring on touchpads.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, onPrev, onNext]);
  const slide = slides[slideIdx];
  const slideCount = slides.length;
  return (
    <div className="ep-lightbox" role="dialog" aria-modal="true" aria-label={t('editPost.slidePreview')} onClick={onClose}>
      <div className="ep-lightbox-content" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="ep-lightbox-close" onClick={onClose} aria-label={t('editPost.closePreview')}>
          <I.x size={18} />
        </button>
        {slide?.isVideo && slide?.publicUrl ? (
          <video
            className="ep-lightbox-img dv-player"
            src={slide.publicUrl}
            {...(slide.posterUrl ? { poster: slide.posterUrl } : {})}
            muted
            loop
            playsInline
            preload="metadata"
            controls
          />
        ) : slide?.imageUrl ? (
          <img
            src={slide.imageUrl}
            alt={slide?.cap || t('editPost.slideAlt', { number: slideIdx + 1 })}
            className="ep-lightbox-img"
          />
        ) : (
          <div className="ep-lightbox-placeholder">{t('editPost.noImageForSlide')}</div>
        )}
        {slideCount > 1 && (
          <>
            <button
              type="button"
              className="ep-lightbox-nav ep-lightbox-nav--prev"
              onClick={onPrev}
              disabled={slideIdx <= 0}
              aria-label={t('editPost.previousSlide')}
            >
              <I.chevLeft size={22} />
            </button>
            <button
              type="button"
              className="ep-lightbox-nav ep-lightbox-nav--next"
              onClick={onNext}
              disabled={slideIdx >= slideCount - 1}
              aria-label={t('editPost.nextSlide')}
            >
              <I.chevRight size={22} />
            </button>
            <div className="ep-lightbox-counter mono">{slideIdx + 1} / {slideCount}</div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Main screen ---------------- */
export const EditPost = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setActivity } = useActivity();
  const dash = useDashboardData() || {};
  const readOwner = api.getReadOwnerScope?.() || dash.accountId || '';
  const creationAllowed = creationAccessState(dash) === 'allowed';
  const { openUpgrade } = useUpgrade();
  const imageAllowance = deriveImageAllowance(dash.entitlements);
  const entitlementsChecked = Boolean(dash.fetchedAt?.entitlements);
  const imageAvailabilityError = !imageAllowance.known && entitlementsChecked && !dash.refreshing;
  const imageGenerationDisabled = !creationAllowed || !imageAllowance.known || imageAllowance.zero;
  // TASK-320 parity: same "both allowances confirmed zero" rule as
  // CreatePost.jsx — hides the Generation Level picker on both regenerate
  // flows here. Not derived from imageGenerationDisabled (asymmetric:
  // "unknown" already counts as disabled for images, not for text).
  const textAllowance = deriveTextAllowance(dash.entitlements);
  const textGenerationDisabled = !creationAllowed || !textAllowance.known || textAllowance.zero;
  const aiFullyUnavailable = imageAllowance.known && imageAllowance.zero && textAllowance.known && textAllowance.zero;
  const { startOperation, updateOperation, completeOperation, failOperation, registerPoller, activeOperations } = useGenerationStatus();

  // Platform/target hint from the URL. Library and PostPreview append
  // ?platform=telegram so we can scope this Edit view to a single platform —
  // critical for batches that fan out across IG + Telegram + Threads, where
  // each content_item in Library represents one platform's variant but the
  // underlying generation job still carries all targets.
  const platformHint = useMemo(
    () => PLATFORM_FROM_NAME(searchParams.get('platform')) || null,
    [searchParams]
  );
  const targetIdHint = searchParams.get('target') || null;
  const saveIdentity = JSON.stringify([readOwner, id, targetIdHint]);
  const saveIdentityRef = useRef(saveIdentity);
  saveIdentityRef.current = saveIdentity;
  useEffect(() => {
    saveIdentityRef.current = saveIdentity;
    return () => { saveIdentityRef.current = null; };
  }, [saveIdentity]);
  const saveScopeCurrent = () => saveIdentityRef.current === saveIdentity
    && (api.getReadOwnerScope?.() || dash.accountId || '') === readOwner;

  // Loaded post state
  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [caption, setCaption] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');
  // Alt text removed from UI per user request — was rarely useful and
  // confused the regenerate copy flow. Backend still accepts the field
  // on manual edit so older clients don't break.
  // Optional "what to change" prompt for text regeneration. Empty = ask
  // model to improve clarity (default behavior); non-empty = pass to
  // backend as additional instructions for the regen LLM call.
  const [regenNotes, setRegenNotes] = useState('');
  // Optional ISO language override for THIS text regen. Empty string =
  // keep the post's stored job.language. Set via the inline pill picker
  // next to Regenerate text — mirrors the image-regen UX exactly so the
  // user doesn't have to learn two different patterns for the same
  // concept ("which language should the AI rewrite this in?").
  const [regenTextLanguage, setRegenTextLanguage] = useState('');
  // Image-regen optional inputs (separate from regenNotes so the user
  // can keep both a text-tweak note and an image-tweak note around
  // without one stomping the other when they switch tabs).
  const [regenImageNotes, setRegenImageNotes] = useState('');
  const [regenImageLanguage, setRegenImageLanguage] = useState('');
  // TASK-236 (#354, EPIC-023 pricing v2) — Economy/Standard/Premium, shared
  // between the text and image regen actions below (one per-user preference,
  // same lib/generationLevel.js localStorage key CreatePost uses).
  const [generationLevel, setGenerationLevel] = useGenerationLevel();
  // Owner decision 2026-09-08 — see CreatePost.jsx / lib/flags.js's
  // generationLevelPickerEnabled: while this reads false (default), both
  // pickers below stay unmounted AND `level`/`metadata.level` are omitted
  // from the regenerate / regenerate-image request bodies entirely.
  const generationLevelPickerVisible = generationLevelPickerEnabled();
  // (Removed regenImageCustomizeOpen state — the customize panel was
  // replaced with always-visible inline controls so there's no expander
  // to track. See ep-regen-modifiers in the JSX below.)
  // Whether to regenerate ONLY the currently selected slide (single) or
  // the entire carousel. Default = single when a carousel exists with
  // >1 slide; user can toggle to "all slides" if they want a full reset.
  const [regenImageScope, setRegenImageScope] = useState('single');
  // Local-only flags so the user sees something is happening while we
  // POST /regenerate or /regenerate-image. The activity-pill at the
  // top is already shown via setActivity, but a button-level spinner
  // is more obvious because it's right next to the trigger.
  const [regenTextBusy, setRegenTextBusy] = useState(false);
  const [regenImageBusy, setRegenImageBusy] = useState(false);
  const [uploadImageBusy, setUploadImageBusy] = useState(false);
  // TASK-052: pick existing Media-library images instead of uploading a new file.
  const { track: trackUpload } = useUploadProgress();
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [attachMediaBusy, setAttachMediaBusy] = useState(false);
  // Reorder autosave indicator — appears briefly after a successful
  // drag-drop persist so the user knows it stuck.
  const [reorderSavedAt, setReorderSavedAt] = useState(0);
  // Two-step delete confirm: holds the slide id currently in the
  // "click again to confirm" state. Cleared on success, on cancel,
  // or on a 4-second timeout if the user wanders off.
  const [confirmingDeleteSlideId, setConfirmingDeleteSlideId] = useState(null);
  const [deletingSlideId, setDeletingSlideId] = useState(null);
  // Fullscreen preview overlay on the right-side MiniPreview. Tied to
  // the same slideIdx state as Media tab, so flipping inside the
  // lightbox also moves the Media tab selection — single source of
  // truth across both panes (one of the user's explicit requests).
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // platforms used to hold platform IDs ("instagram", "threads"), which broke
  // multi-account workspaces — user couldn't pick which of two Telegrams to
  // publish to, and the auto-select inferred the wrong account at random.
  // Now stores accountIds; platform is derived from the account when needed.
  const [selectedAccounts, setSelectedAccounts] = useState(new Set());
  // All connected accounts for this user, grouped by platform — populated
  // once on mount so the schedule picker can render one row per account.
  const [allAccounts, setAllAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [slides, setSlides] = useState([]);
  const [slideIdx, setSlideIdx] = useState(0);
  // Which slides are mid-regeneration *in this session* — set when the user
  // triggers a regen here ('all' or a slide index), cleared when it settles.
  // Combined with the server-tracked op below so the busy/greyed state also
  // holds after a reload and shows in other tabs / the Library preview.
  const [regenTargets, setRegenTargets] = useState(null);
  // True while an image regen for THIS post is in flight — locally (this tab)
  // OR per the server's async_operations feed (survives reload + cross-tab,
  // which is what stops the button re-enabling after a refresh).
  const regenActiveServer = (activeOperations || []).some(
    (op) => op.kind === 'visual' && op.status === 'running'
      && op.sourceRef?.type === 'contentItem' && op.sourceRef?.id === id
  );
  // Seeded by a one-shot activeOperations fetch on (re)load so a page reloaded
  // mid-regen knows IMMEDIATELY that a regen is running — before the global
  // context poll catches up. opsChecked gates the Regenerate button until that
  // first check returns (closes the brief clickable window after a refresh);
  // loadRegenSeed is handed off to the live context state shortly after.
  const [opsChecked, setOpsChecked] = useState(false);
  const [loadRegenSeed, setLoadRegenSeed] = useState(false);
  const regenActive = regenImageBusy || regenActiveServer || loadRegenSeed;
  // A given slide is greyed if we know the in-session target (single→that
  // index, all→every slide). If we only know from the server (post-reload,
  // slide index not disclosed there) grey them all.
  const isSlideRegenerating = (i) => regenActive && (regenTargets == null || regenTargets === 'all' || regenTargets === i);
  const [scheduleMode, setScheduleMode] = useState('scheduled');
  const [scheduledAt, setScheduledAt] = useState('');
  // Initial tab honors ?tab= so deep-links from PostPreview ("Schedule this
  // post" / "Reschedule") land directly on the schedule tab.
  const initialTab = (() => {
    if (typeof window === 'undefined') return 'copy';
    const t = new URLSearchParams(window.location.search).get('tab');
    return ['copy', 'media', 'schedule'].includes(t) ? t : 'copy';
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [saving, setSaving] = useState(false);
  const savePendingRef = useRef(false);
  const [confirmedUnpublished, setConfirmedUnpublished] = useState(false);
  const [republishConfirmation, setRepublishConfirmation] = useState(null);
  const alreadyPublishedIds = useMemo(() => publishedAccountIds(raw), [raw]);
  const resolvedTargetId = raw?.targetResolution?.requestedTargetId === targetIdHint
    ? raw.targetResolution.resolvedTargetId : targetIdHint;
  const selectedTarget = targetIdHint
    ? raw?.publishSummary?.targets?.find((target) => target.id === resolvedTargetId) || null
    : null;
  const selectedTargetMissing = Boolean(raw && targetIdHint && !selectedTarget);
  const recoverySource = selectedTarget && (
    ['failed', 'needs_reconnect'].includes(selectedTarget.status)
    || (selectedTarget.status === 'needs_manual_action' && selectedTarget.lastErrorCode === 'PUBLISH_OUTCOME_UNKNOWN')
  ) ? selectedTarget : null;
  const recoveryCandidates = Array.isArray(raw?.recoveryCandidates) ? raw.recoveryCandidates : [];

  useEffect(() => {
    dash.ensureDashboardData?.().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture originals to compute isDirty
  const [originals, setOriginals] = useState({ caption: '', hashtags: '', scheduledAt: '' });

  // Load connected social accounts once so the schedule picker can render
  // one row per account (and we can let the user re-target a different
  // account on the same network — common when two Telegrams or two IGs are
  // connected to one workspace).
  useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    setAllAccounts([]);
    api.socialAccounts.list().then((res) => {
      if (cancelled) return;
      const list = res?.accounts || res?.data?.accounts || [];
      setAllAccounts(list.filter((a) => a.status === 'connected'));
    }).catch(() => {}).finally(() => {
      if (!cancelled) setAccountsLoading(false);
    });
    return () => { cancelled = true; };
  }, [readOwner]);

  // Fetch post
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRaw(null);
    api.content.get(id, { fresh: true, targetId: targetIdHint })
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setRaw(payload);
        // Hydrate form from server data
        const item = payload?.contentItem || payload;
        const v = payload?.activeVersion;
        const targets = payload?.publishSummary?.targets || [];
        const cap = cleanPromptPrefix(v?.body || v?.caption || item?.body || '');
        const hookOrHash = (v?.hashtags && Array.isArray(v.hashtags) ? v.hashtags : []).join(' ');
        setCaption(cap);
        setHashtagsInput(hookOrHash);
        // NOTE: account pre-selection lives in a separate effect below — it
        // needs allAccounts to resolve `platformHint` filtering (the URL hint
        // says "this is the Telegram variant", so we must drop the IG and
        // Threads peers from the originating job's target list).
        const resolvedId = payload?.targetResolution?.requestedTargetId === targetIdHint
          ? payload.targetResolution.resolvedTargetId : targetIdHint;
        const repTarget = targetIdHint
          ? targets.find((target) => target.id === resolvedId)
          : targets[0];
        const sched = repTarget?.status === 'published' ? ''
          : repTarget?.scheduledAt || payload?.publishSummary?.lastScheduledAt || '';
        const schedLocal = toLocalDatetimeInput(sched);
        setScheduledAt(schedLocal);
        const slidesRaw = payload?.carouselSlides || [];
        const mediaUrls = payload?.mediaUrls || [];
        let mappedSlides;
        if (slidesRaw.length > 0) {
          mappedSlides = slidesRaw.map((s, i) => ({
            id: s.id || `s${i}`,
            kind: 'image',
            cap: s.caption || '',
            ...slideVideoFields(s.mediaAsset),
            imageUrl: isVideoAsset(s.mediaAsset) ? null : (s.mediaAsset?.publicUrl || s.imageUrl || mediaUrls[i] || null),
            grad: ['#888', '#444'],
            serif: false,
            tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
          }));
        } else if (mediaUrls.length > 0) {
          mappedSlides = mediaUrls.map((url, i) => ({
            id: `s${i}`,
            kind: 'image',
            cap: '',
            imageUrl: url,
            grad: ['#888', '#444'],
            serif: false,
            tag: '',
          }));
        } else {
          mappedSlides = [{
            id: 's0', kind: 'image', cap: '',
            imageUrl: v?.imageUrl || payload?.mediaPreview?.url || null,
            grad: ['#888', '#444'], serif: false, tag: '',
          }];
        }
        setSlides(mappedSlides);
        setOriginals({ caption: cap, hashtags: hookOrHash, scheduledAt: schedLocal });
      })
      .catch((err) => {
        if (cancelled) return;
        setRaw(null);
        setError(errMessage(err) || t('editPost.couldNotLoadPost'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, targetIdHint, readOwner]);

  // Keep published accounts opt-in, including history from earlier schedules.
  const [accountsInitialized, setAccountsInitialized] = useState(false);
  useEffect(() => {
    setAccountsInitialized(false);
    setConfirmedUnpublished(false);
  }, [id, targetIdHint, readOwner]);
  useEffect(() => {
    if (accountsInitialized || !raw || accountsLoading) return;
    setSelectedAccounts(initialPostAccounts({ post: raw, accounts: allAccounts, platformHint, targetIdHint: resolvedTargetId }));
    setAccountsInitialized(true);
  }, [raw, allAccounts, platformHint, resolvedTargetId, accountsInitialized, accountsLoading]);

  // Parsed hashtags from input
  const hashtags = useMemo(() => {
    return hashtagsInput
      .split(/\s+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.startsWith('#') ? t : `#${t}`));
  }, [hashtagsInput]);

  const isDirty = useMemo(() => {
    return caption !== originals.caption
      || hashtagsInput !== originals.hashtags
      || scheduledAt !== originals.scheduledAt;
  }, [caption, hashtagsInput, scheduledAt, originals]);

  const toggleAccount = (accountId) => {
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  // Derived: distinct platforms across the picked accounts. Used by the
  // char-limit and preview-platform helpers below; selectedAccounts is
  // the canonical source of "what will publish" now.
  const selectedPlatforms = useMemo(() => {
    const set = new Set();
    selectedAccounts.forEach((accId) => {
      const acc = allAccounts.find((a) => a.id === accId);
      if (acc?.platform) set.add(acc.platform);
    });
    return set;
  }, [selectedAccounts, allAccounts]);

  const togglePlatform = (id) => {
    // Legacy: kept for the copy-tab platform selector. Toggles ALL accounts
    // for a platform on/off (caller doesn't care about per-account here).
    const accountsForPlatform = allAccounts.filter((a) => a.platform === id);
    setSelectedAccounts((prev) => {
      const next = new Set(prev);
      const anyOn = accountsForPlatform.some((a) => next.has(a.id));
      if (anyOn) {
        accountsForPlatform.forEach((a) => next.delete(a.id));
      } else {
        accountsForPlatform.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const charLimit = useMemo(() => {
    let min = Infinity;
    selectedPlatforms.forEach((id) => {
      const limit = getCharacterLimit(id);
      if (limit < min) min = limit;
    });
    return Number.isFinite(min) ? min : 2200;
  }, [selectedPlatforms]);

  const currentPlatform = useMemo(() => {
    // Prefer the URL hint so the live preview stays on Telegram chrome even
    // if the user momentarily toggles all accounts off.
    const first = platformHint || Array.from(selectedPlatforms)[0] || 'instagram';
    const def = PLATFORM_DEFS.find((p) => p.id === first) || PLATFORM_DEFS[0];
    return { ...def, tint: BrandTints[def.id] };
  }, [selectedPlatforms, platformHint]);

  // Pick the handle from a target whose platform matches the hint (so the
  // preview shows "@dathentai_tg" not "@dathentai_ig" when viewing the
  // Telegram variant of a multi-platform batch).
  const handle = useMemo(() => {
    const targets = raw?.publishSummary?.targets || [];
    if (platformHint) {
      const matched = targets.find((t) => PLATFORM_FROM_NAME(t.platform) === platformHint);
      if (matched?.socialAccount?.displayName) return matched.socialAccount.displayName;
    }
    return targets[0]?.socialAccount?.displayName || '';
  }, [raw, platformHint]);

  // Whether the loaded post is already in draft state. When true, the
  // "Save as draft" radio is non-functional (just re-saves text edits, no
  // scheduling side effect) and confuses users into thinking it spawns a
  // second draft. We hide / disable it in that case.
  const isDraft = useMemo(() => {
    const item = raw?.contentItem || raw;
    const status = item?.status;
    return status === 'draft' || status === 'ready';
  }, [raw]);

  // Whether this post currently has an active scheduled_post to cancel.
  // Used by canSaveAsDraft below (bug 2026-09-10): picking "Save as draft"
  // on a scheduled post is itself an action (handleSave unschedules it)
  // even when the user made no text edits.
  const hasScheduledPost = Boolean(selectedTarget?.scheduledPostId || raw?.publishSummary?.scheduledPostId);

  const canSaveDraftAction = canSaveAsDraft({ isDirty, isDraft, scheduleMode, hasScheduledPost });

  // If we landed on a draft and the user is on the Schedule tab, default to
  // "Schedule" mode rather than "Save as draft" so the picker actually does
  // something meaningful on Save.
  useEffect(() => {
    if (isDraft && scheduleMode === 'draft') {
      setScheduleMode('scheduled');
    }
    // intentionally not depending on scheduleMode so a user who switches
    // back to draft is not yanked away again. Only runs on isDraft flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraft]);

  const moveSlide = (idx, dir) => {
    const next = [...slides];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setSlides(next);
    setSlideIdx(target);
    // Autosave the new order — no Save button on media tab. If the post
    // hasn't been hydrated yet (slides are local-only "s0"/"s1" ids),
    // persistSlideOrder bails out internally.
    persistSlideOrder(next);
  };

  // Drag-and-drop reorder: pluck slide at `from`, splice it into `to`.
  // Active-slide index follows so the live preview stays on the slide
  // the user was looking at.
  const reorderSlide = (from, to) => {
    if (from === to) return;
    const next = [...slides];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSlides(next);
    setSlideIdx((current) => {
      if (current === from) return to;
      if (from < current && current <= to) return current - 1;
      if (to <= current && current < from) return current + 1;
      return current;
    });
    persistSlideOrder(next);
  };

  const handleSave = async ({ republishAccountIds = [] } = {}) => {
    if (!creationAllowed) return;
    if (!id || saving || savePendingRef.current) return;
    if (selectedTargetMissing) {
      notify({ level: 'warning', title: t('editPost.selectedPublicationUnavailable') });
      return;
    }
    const timeError = scheduleTimeError(scheduleMode, scheduledAt);
    if (timeError) {
      setActiveTab('schedule');
      notify({ level: 'warning', title: t(`editPost.${timeError}`) });
      return;
    }

    // Build targets up-front so we can include them in the optimistic op label.
    const targets = Array.from(selectedAccounts)
      .map((accountId) => {
        const acc = allAccounts.find((a) => a.id === accountId);
        if (!acc) return null;
        return { platform: acc.platform, socialAccountId: acc.id };
      })
      .filter(Boolean);

    if ((scheduleMode === 'scheduled' || scheduleMode === 'now') && targets.length === 0) {
      notify({ level: 'warning', title: t('editPost.selectAccountAlert') });
      return;
    }

    if (recoverySource && scheduleMode !== 'draft') {
      if (!recoverySource.scheduledPostId
        || targets.length !== 1
        || targets[0].platform !== recoverySource.platform
        || targets[0].socialAccountId !== recoverySource.socialAccountId) {
        notify({ level: 'warning', title: t('editPost.recoverySameAccountOnly') });
        return;
      }
      if (recoveryCandidates.length > 0) {
        notify({ level: 'warning', title: t('editPost.recoveryExistingPublication') });
        return;
      }
      if (!confirmedUnpublished) {
        notify({ level: 'warning', title: t('editPost.confirmUnpublishedRequired') });
        return;
      }
    }
    const recoveryRequest = recoverySource && scheduleMode !== 'draft'
      ? { recoverySourceTargetId: recoverySource.id, confirmUnpublished: true }
      : {};

    const repeatIds = scheduleMode === 'draft' ? []
      : targets.filter((target) => alreadyPublishedIds.has(target.socialAccountId)).map((target) => target.socialAccountId);
    if (repeatIds.some((accountId) => !republishAccountIds.includes(accountId))) {
      setRepublishConfirmation({ accountIds: repeatIds });
      return;
    }
    setRepublishConfirmation(null);

    // publish_now flow gets optimistic navigation + global status plate.
    // Saving text and scheduling text-only updates stay synchronous because
    // they're quick and don't deserve a global tracker.
    const isPublishNow = scheduleMode === 'now';

    if (isPublishNow) {
      savePendingRef.current = true;
      const opId = startPublishNowOperation(startOperation, {
        contentId: id,
        scheduledPostId: selectedTarget?.scheduledPostId || raw?.publishSummary?.scheduledPostId || null,
        targets,
        navigate,
      });
      // Hop straight to the post preview so the user sees the status plate
      // + the preview's in-flight overlay immediately. The publish API call
      // continues in background and updates the op via update/fail.
      const initialHint = targetIdHint ? `?target=${encodeURIComponent(targetIdHint)}` : '';
      navigate(`/app/post/${encodeURIComponent(id)}${initialHint}`);
      runPublishNowInBackground({ opId, targets, republishAccountIds, recoveryRequest });
      return;
    }

    savePendingRef.current = true;
    setSaving(true);
    setActivity({ kind: 'planning', step: t('editPost.savingChanges'), project: t('editPost.editPostActivity') });
    try {
      let currentVersionId = raw?.activeVersion?.id;
      let savedNewVersion = false;
      let createdTargetId = null;

      if (caption !== originals.caption || hashtagsInput !== originals.hashtags) {
        const editResult = await api.content.createManualEdit(id, {
          body: caption,
          caption,
          hashtags,
        });
        const editedVersionId = editResult?.data?.activeVersion?.id;
        savedNewVersion = Boolean(editedVersionId && editedVersionId !== currentVersionId);
        currentVersionId = editedVersionId || currentVersionId;
      }

      const scheduledPostId = selectedTarget?.scheduledPostId || raw?.publishSummary?.scheduledPostId;

      if (scheduleMode === 'scheduled' && currentVersionId) {
        const localDate = new Date(scheduledAt);
        // Recheck after copy saves; a time that elapsed must never become "now".
        if (scheduleTimeError(scheduleMode, scheduledAt)) throw new Error(t('editPost.timePassedHint'));
        if (scheduledPostId) {
          const existingTargets = raw?.publishSummary?.targets || [];
          // A time-only reschedule keeps its historical content version.
          // After saving new copy, replace only the selected account targets
          // so they use the edit; unselected siblings keep their own snapshot.
          if (recoverySource || savedNewVersion || requiresNewSchedule(existingTargets, targets, alreadyPublishedIds)) {
            const cloneResult = await api.scheduler.clone(scheduledPostId, {
              targets,
              republishAccountIds,
              ...recoveryRequest,
              scheduleMode: 'schedule',
              scheduledAt: localDate.toISOString(),
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            });
            if (targets.length === 1) createdTargetId = newTargetIdFromClone(cloneResult, targets[0].socialAccountId);
          } else {
            // Reschedule semantics:
            //   - No targetIds  →  backend updates every active target
            //     under this scheduled_post (full move; original behavior).
            //   - targetIds=[…] →  backend only updates THOSE specific
            //     publish_targets; sibling targets keep their time.
            //
            // We narrow to specific target IDs whenever the user's
            // current account selection is a strict SUBSET of the
            // scheduled_post's targets — meaning they either opened the
            // post with `?target=…` (per-channel scope) OR explicitly
            // un-checked some accounts in the picker. Either way, the
            // intent is "only move what I have selected, leave the rest
            // alone." Sibling targets (e.g. the second Telegram channel
            // the user did NOT click into) stay on their original time.
            const selectedAccountIds = new Set(targets.map((t) => t.socialAccountId).filter(Boolean));
            const keptTargetIds = existingTargets
              .filter((t) => t.socialAccountId && selectedAccountIds.has(t.socialAccountId))
              .map((t) => t.id)
              .filter(Boolean);
            const isPartial = keptTargetIds.length > 0
              && keptTargetIds.length < existingTargets.length;
            await api.scheduler.reschedule(scheduledPostId, {
              scheduledAt: localDate.toISOString(),
              ...(isPartial ? { targetIds: keptTargetIds } : {}),
            });
          }
        } else {
          await api.scheduler.create({
            contentItemId: id,
            contentVersionId: currentVersionId,
            scheduledAt: localDate.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
            mode: 'manual',
            targets,
          });
        }
      }

      // "Save as draft": revert a scheduled post back to a draft. The
      // scheduleMode='draft' radio existed but did nothing here, so a scheduled
      // post had no way back to draft. unschedule cancels the scheduled_post +
      // its active publish_targets and re-syncs the content status, so the post
      // drops out of "scheduled" and lists as a draft. Copy edits above are
      // already saved; this is a no-op when the post was never scheduled.
      if (scheduleMode === 'draft' && scheduledPostId) {
        await api.scheduler.unschedule(scheduledPostId);
      }

      if (!saveScopeCurrent()) return;
      setActivity({ kind: 'done', message: t('editPost.postSaved') });
      // Carry a "?just=scheduled" hint to PostPreview so it knows to do
      // one additional cache-busting refresh ~1.5s after mount. The
      // scheduler.create call already invalidates the content cache, but
      // D1's read replicas can lag the primary by a few hundred ms — if
      // the PostPreview fetch races the replica catching up, it sees
      // content_items.status='ready' (pre-schedule) AND publishSummary
      // without the brand-new scheduled_post. The hint triggers a single
      // delayed refetch which catches that case without polling on every
      // PostPreview mount.
      const targetParams = new URLSearchParams();
      if (scheduleMode === 'scheduled') targetParams.set('just', 'scheduled');
      if (createdTargetId || targetIdHint) targetParams.set('target', createdTargetId || targetIdHint);
      const targetSuffix = targetParams.size ? `?${targetParams.toString()}` : '';
      navigate(`/app/post/${encodeURIComponent(id)}${targetSuffix}`);
    } catch (err) {
      console.error('Save error:', err);
      if (saveScopeCurrent()) {
        setActivity(null);
        notify({ level: 'error', title: t('editPost.saveFailed', { error: friendlyErrorMessage(err) }) });
      }
    } finally {
      setSaving(false);
      savePendingRef.current = false;
    }
  };

  // publish_now background runner — keeps the op alive in
  // GenerationStatusContext while the API call resolves. Failures land in
  // the plate as red rows with the error message (not in window.alert,
  // since the user has already navigated away).
  const runPublishNowInBackground = async ({ opId, targets, republishAccountIds, recoveryRequest }) => {
    try {
      let currentVersionId = raw?.activeVersion?.id;
      if (caption !== originals.caption || hashtagsInput !== originals.hashtags) {
        const editResult = await api.content.createManualEdit(id, {
          body: caption,
          caption,
          hashtags,
        });
        currentVersionId = editResult?.data?.activeVersion?.id || currentVersionId;
      }
      if (!currentVersionId) throw new Error(t('editPost.missingContentVersion'));

      const scheduledPostId = selectedTarget?.scheduledPostId || raw?.publishSummary?.scheduledPostId;
      const nowIso = new Date().toISOString();
      let canonicalScheduledPostId = scheduledPostId;
      let newTargetId = null;
      if (scheduledPostId) {
        // Always clone for publish_now from an existing sp. The clone
        // endpoint atomically:
        //   1. Creates a fresh scheduled_post with new publish_targets
        //   2. Cancels any still-active targets on the source sp that
        //      would conflict (cancelActiveScheduledTargetsForPublishNow)
        //   3. Dispatches the new targets immediately
        // This is the correct semantic for "Edit & republish" — the
        // original published row stays in history, the new one carries
        // the user's edits + new account selection.
        const cloneResult = await api.scheduler.clone(scheduledPostId, {
          targets,
          republishAccountIds,
          ...recoveryRequest,
          scheduleMode: 'publish_now',
          scheduledAt: nowIso,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        });
        canonicalScheduledPostId = cloneResult?.data?.post?.id || cloneResult?.post?.id || null;
        newTargetId = targets.length === 1 ? newTargetIdFromClone(cloneResult, targets[0].socialAccountId) : null;
      } else {
        const createResult = await api.scheduler.create({
          contentItemId: id,
          contentVersionId: currentVersionId,
          scheduledAt: nowIso,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          mode: 'publish_now',
          targets,
        });
        canonicalScheduledPostId = createResult?.data?.post?.id || createResult?.post?.id || null;
      }

      // Upgrade the op's sourceRef to scheduledPost so the ContentFeed
      // bridge can match it against target rows.
      if (canonicalScheduledPostId) {
        updateOperation(opId, {
          status: 'running',
          sourceRef: { type: 'scheduledPost', id: canonicalScheduledPostId },
          ...(newTargetId ? { deeplink: `/app/post/${encodeURIComponent(id)}?target=${encodeURIComponent(newTargetId)}` } : {}),
        });
      } else {
        updateOperation(opId, { status: 'running' });
      }
    } catch (err) {
      console.error('publish_now background error:', err);
      failOperation(opId, errMessage(err) || t('editPost.publishFailed'));
    } finally {
      savePendingRef.current = false;
    }
  };

  // Post delete uses the styled ConfirmDialog instead of a raw window.confirm.
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = () => setConfirmDeleteOpen(true);

  const performDelete = async () => {
    setDeleting(true);
    try {
      await api.content.archive(id);
      navigate('/app/library');
    } catch (err) {
      notify({ level: 'error', title: t('editPost.deleteFailed', { error: friendlyErrorMessage(err) }) });
      setDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  /* Rebuild the post + its slide strip from the server after ANY media change
     (upload, attach-from-library). setRaw alone does not rebuild the local
     `slides` array — that array is the strip in the Media tab and the preview,
     and leaving it stale is what once made "Uploading…" hang forever. */
  const reloadPostAfterMediaChange = async () => {
    const res = await api.content.get(id, { targetId: targetIdHint, fresh: true });
    const payload = res?.data && typeof res.data === 'object' ? res.data : res;
    if (!payload) return;
    setRaw(payload);
    const slidesRaw = payload?.carouselSlides || [];
    if (slidesRaw.length > 0) {
      setSlides(slidesRaw.map((s, i) => ({
        id: s.id || `s${i}`,
        kind: 'image',
        cap: s.caption || '',
        ...slideVideoFields(s.mediaAsset),
        imageUrl: isVideoAsset(s.mediaAsset) ? null : (s.mediaAsset?.publicUrl || s.imageUrl || null),
        grad: ['#888', '#444'],
        serif: false,
        tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
      })));
      // Focus the last slide: the one the user just added.
      setSlideIdx(Math.max(0, slidesRaw.length - 1));
    }
  };

  /* TASK-052 — attach images the user already has in the Media library.
     Complements "upload custom image": same destination (the post's slides),
     no re-upload of an asset that already exists. Appends, matching the
     upload flow's semantics; the server also releases a post held by the
     no-visuals gate once media lands. */
  const handleAttachFromLibrary = async (chosen) => {
    if (!creationAllowed) return;
    setLibraryPickerOpen(false);
    const ids = (chosen || []).map((m) => m?.id).filter(Boolean);
    if (!ids.length || !id) return;
    setAttachMediaBusy(true);
    try {
      setActivity({
        kind: 'active',
        step: t('editPost.attachingMedia'),
        project: t('editPost.editPostActivity'),
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: t('editPost.openMedia'),
      });
      const res = await api.content.attachMedia(id, ids, { mode: 'attach' });
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      setActivity({
        kind: 'done',
        message: data?.released
          ? t('editPost.mediaAttachedReleased')
          : t('editPost.mediaAttached', { count: ids.length }),
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: t('editPost.openMedia'),
      });
      await reloadPostAfterMediaChange();
    } catch (error) {
      setActivity({ kind: 'error', message: friendlyErrorMessage(error) });
      notify({ level: 'error', title: friendlyErrorMessage(error) });
    } finally {
      setAttachMediaBusy(false);
    }
  };

  const handleUploadCustomImage = async (event) => {
    if (!creationAllowed) return;
    const file = event?.target?.files?.[0];
    if (!file || !id) return;
    // Reset the input so picking the same file twice in a row still triggers
    // a fresh upload.
    event.target.value = '';
    if (!file.type?.startsWith('image/')) {
      notify({ level: 'warning', title: t('editPost.pickImageFile') });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notify({ level: 'warning', title: t('editPost.imageTooLarge') });
      return;
    }
    setUploadImageBusy(true);
    // Optimistic UI: append a placeholder slide thumb to the strip so
    // the user immediately sees their action register. The actual
    // media_asset arrives via setRaw → useEffect → setSlides cascade
    // and replaces this placeholder. If the upload fails, the catch
    // block removes the placeholder.
    const placeholderId = `s_uploading_${Date.now()}`;
    const previousSlidesLength = slides.length;
    setSlides((prev) => [...prev, {
      id: placeholderId,
      kind: 'image',
      cap: '',
      imageUrl: null,
      grad: ['#1f2937', '#0f172a'],
      serif: false,
      uploading: true,
      tag: `${String(prev.length + 1).padStart(2, '0')} / ${String(prev.length + 1).padStart(2, '0')}`,
    }]);
    // Jump the active slide to the placeholder so the right-side preview
    // and Media tab both center on the in-progress upload.
    setSlideIdx(previousSlidesLength);
    try {
      setActivity({
        kind: 'active',
        step: t('editPost.uploadingNewSlide'),
        project: t('editPost.editPostActivity'),
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: t('editPost.openMedia'),
      });
      // APPEND a new slide rather than replace the currently-selected
      // one. Users expect "Upload custom image" to add to the carousel
      // — replace-this-slide is a separate semantic (handled by the
      // regen flow with scope=single). For single-slide posts this
      // creates slide 2 and effectively upgrades them to a carousel.
      const appendedIndex = previousSlidesLength + 1;
      await trackUpload(
        file.name,
        (onProgress) => api.content.uploadCustomImage(id, file, { slideIndex: appendedIndex, onProgress }),
        { size: file.size }
      );
      setActivity({
        kind: 'done',
        message: t('editPost.slideAdded', { number: appendedIndex }),
        returnTo: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
        returnLabel: t('editPost.openMedia'),
      });
      // Reload just the post (no full page refresh) so other state
      // — selected schedule, active tab, dirty edits — survives.
      // We explicitly rebuild `slides` from the fresh payload here —
      // setSlides was last set with a placeholder so we can't rely on
      // any effect to swap it; setRaw alone doesn't rebuild the local
      // slides array (which is the user-facing strip in the Media tab
      // and the right-side preview). This was the source of the
      // "Uploading…" hanging forever bug — placeholder never got
      // replaced.
      await reloadPostAfterMediaChange();
    } catch (err) {
      // Roll back the optimistic placeholder.
      setSlides((prev) => prev.filter((s) => s.id !== placeholderId));
      setSlideIdx(previousSlidesLength > 0 ? previousSlidesLength - 1 : 0);
      setActivity(null);
      console.error('Upload error:', err);
      notify({ level: 'error', title: t('editPost.uploadFailed', { error: friendlyErrorMessage(err) }) });
    } finally {
      setUploadImageBusy(false);
    }
  };

  // Regenerate the TEXT (caption/hook/cta) using the post's existing
  // platform + language settings. If user typed something into the
  // regen-notes field, that's passed as additional instructions to the
  // LLM (e.g. "make it shorter and add a question at the end").
  const handleRegenerate = async () => {
    if (regenTextBusy || textGenerationDisabled) return;
    setRegenTextBusy(true);
    // Mint a global op so progress shows in the floating Activity plate and
    // survives navigating away mid-regen — same contract as image regen.
    const textOpId = startOperation({
      kind: 'single_draft',
      label: t('editPost.regeneratingTextLabel'),
      status: 'running',
      total: null,
      sourceRef: { type: 'contentItem', id },
      deeplink: `/app/post/${encodeURIComponent(id)}/edit?tab=copy`,
    });
    try {
      const body = { confirmedCreditSpend: true, ...generationLevelPayloadFields(generationLevel, generationLevelPickerVisible) };
      const trimmed = regenNotes.trim();
      if (trimmed) body.instructions = trimmed;
      if (regenTextLanguage) body.language = regenTextLanguage;
      const res = await api.content.regenerate(id, body);
      // Refresh local state immediately so user sees the new text. The
      // backend returns the new version inline.
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const v = payload?.activeVersion;
      if (v) {
        setCaption(cleanPromptPrefix(v.body || v.caption || ''));
        const tags = Array.isArray(v.hashtags) ? v.hashtags : [];
        setHashtagsInput(tags.join(' '));
        setOriginals((o) => ({
          ...o,
          caption: cleanPromptPrefix(v.body || v.caption || ''),
          hashtags: tags.join(' ')
        }));
      }
      setRaw((prev) => prev ? { ...prev, activeVersion: v ?? prev.activeVersion } : prev);
      setRegenNotes('');
      // returnTo on the done state too — the user might want to jump
      // back to the post even after the regen finishes (e.g. to review
      // the new copy and decide if they want another pass).
      completeOperation(textOpId, { status: 'succeeded' });
    } catch (err) {
      failOperation(textOpId, errMessage(err) || t('editPost.regenerateTextFailed'));
      if (err?.code === 'PLAN_LAPSED') {
        dash.invalidateResource?.('entitlements', { fresh: true });
        openUpgrade({ feature: 'default', variant: 'renew' });
      }
      console.error('Regenerate error:', err);
      notify({ level: 'error', title: t('editPost.regenerateFailed', { error: friendlyErrorMessage(err) }) });
    } finally {
      setRegenTextBusy(false);
    }
  };

  // Regenerate the image(s). When the customize panel is open the user
  // can pass:
  //   instructions — free-text note ("make it brighter", "add a person")
  //   language     — ISO override for image text (Lithuanian etc.)
  //   slideIndex   — set when scope='single' on a multi-slide carousel
  //                  → backend only swaps that one slide and keeps the
  //                  others; preserves the narrative role.
  // Stable signature of the current image(s) so the poller can detect when the
  // server swaps in the regenerated asset. Single-slide regen watches only that
  // slide; otherwise the whole set.
  const regenImageSignature = (content, slideIndex) => {
    const slides = Array.isArray(content?.carouselSlides) ? content.carouselSlides : [];
    const pick = (s) => (s?.mediaAsset?.publicUrl || s?.imageUrl || s?.mediaAsset?.id || s?.mediaAssetId || '');
    if (slides.length) {
      if (slideIndex != null && slideIndex >= 0 && slideIndex < slides.length) return pick(slides[slideIndex]);
      return slides.map(pick).join('|');
    }
    const v = content?.activeVersion || {};
    return v.imageUrl || (Array.isArray(v.mediaUrls) ? v.mediaUrls[0] : '') || '';
  };
  const applyRegenContent = (payload) => {
    if (!payload) return;
    setRaw(payload);
    const slidesRaw = payload?.carouselSlides || [];
    if (slidesRaw.length > 0) {
      setSlides(slidesRaw.map((s, i) => ({
        id: s.id || `s${i}`,
        kind: 'image',
        cap: s.caption || '',
        ...slideVideoFields(s.mediaAsset),
        imageUrl: isVideoAsset(s.mediaAsset) ? null : (s.mediaAsset?.publicUrl || s.imageUrl || null),
        grad: ['#888', '#444'],
        serif: false,
        tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
      })));
    }
  };

  // ── Regen state on (re)load ───────────────────────────────────────────────
  // One immediate activeOperations check so a page reloaded mid-regen knows
  // right away a regen is running (without waiting for the global context
  // poll). Seeds loadRegenSeed (keeps the button disabled) and flips opsChecked
  // (the button gate). The seed is dropped after a short handoff so the live
  // context state takes over and the button isn't stuck disabled once it ends.
  useEffect(() => {
    if (!id) { setOpsChecked(true); return undefined; }
    let cancelled = false;
    setOpsChecked(false);
    setLoadRegenSeed(false);
    api.content.activeOperations()
      .then((res) => {
        if (cancelled) return;
        const ops = res?.data?.operations || res?.operations || [];
        const active = ops.some((op) =>
          op?.status === 'running'
          && (op.kind === 'image_regen' || op.kind === 'visual')
          && (op.contentItemId === id || op.content_item_id === id));
        setLoadRegenSeed(active);
        setOpsChecked(true);
      })
      .catch(() => { if (!cancelled) setOpsChecked(true); });
    const handoff = setTimeout(() => { if (!cancelled) setLoadRegenSeed(false); }, 15000);
    return () => { cancelled = true; clearTimeout(handoff); };
  }, [id]);

  // ── Auto-apply the new image when a regen finishes after a reload ─────────
  // The in-session poller doesn't survive a refresh, so when the server op for
  // THIS post clears (regen done) we refetch the content and swap the image in
  // — no manual reload needed.
  const prevRegenServerRef = useRef(false);
  useEffect(() => {
    if (prevRegenServerRef.current && !regenActiveServer && id) {
      setLoadRegenSeed(false);
      api.content.get(id, { fresh: true, targetId: targetIdHint })
        .then((res) => applyRegenContent(res?.data && typeof res.data === 'object' ? res.data : res))
        .catch(() => {});
    }
    prevRegenServerRef.current = regenActiveServer;
  }, [regenActiveServer, id]);

  // Poll the content item until its image signature changes (server attached
  // the new asset), then apply it + finish the op. Safety timeout fails the op
  // so it never hangs. Registered with the global status context so it survives
  // the user leaving EditPost.
  const pollForRegeneratedImage = (opId, beforeSig, watchIndex, slideCount) => {
    const REGEN_TIMEOUT_MS = 4 * 60 * 1000;
    let unregister = () => {};
    const stop = () => { setRegenImageBusy(false); setRegenTargets(null); unregister(); };
    unregister = registerPoller(opId, async (op) => {
      if (Date.now() - (op?.startedAt || Date.now()) > REGEN_TIMEOUT_MS) {
        failOperation(opId, t('editPost.regenerationTimedOut'));
        stop();
        return;
      }
      let fresh;
      try {
        fresh = await api.content.get(id, { fresh: true, targetId: targetIdHint });
      } catch {
        return; // transient — the poll loop will try again
      }
      const data = fresh?.data && typeof fresh.data === 'object' ? fresh.data : fresh;
      const afterSig = regenImageSignature(data, watchIndex);
      if (afterSig && afterSig !== beforeSig) {
        applyRegenContent(data);
        completeOperation(opId, { status: 'succeeded', completed: slideCount > 1 ? slideCount : 1 });
        stop();
      }
    });
  };

  const handleRegenerateImage = async () => {
    if (regenImageBusy || imageGenerationDisabled) return; // de-dupe + entitlement belt
    setRegenImageBusy(true);
    const carousel = Array.isArray(raw?.carouselSlides) ? raw.carouselSlides : [];
    // Resolve the SELECTED slide's true position in the persisted carousel by
    // its stable id (not just the UI array index) so the regen always targets
    // exactly the slide focused in Media — even if the UI order briefly drifts
    // from the server (e.g. a reorder whose save is still in flight).
    const selectedSlide = slides[slideIdx];
    const matchedIndex = selectedSlide?.id ? carousel.findIndex((c) => c.id === selectedSlide.id) : -1;
    const targetIndex = matchedIndex >= 0 ? matchedIndex : slideIdx;
    const singleScope = regenImageScope === 'single' && carousel.length > 1;
    const slideCount = singleScope ? 1 : carousel.length;
    const watchIndex = singleScope ? targetIndex : null;
    setRegenTargets(singleScope ? slideIdx : 'all');
    const beforeSig = regenImageSignature(raw, watchIndex);
    // Mint a global op so the user sees progress in the floating plate even if
    // they navigate away from EditPost mid-regen.
    const visualOpId = startOperation({
      kind: 'visual',
      label: slideCount > 1 ? t('editPost.regeneratingSlidesLabel', { count: slideCount }) : t('editPost.regeneratingImageLabel'),
      status: 'running',
      total: slideCount > 1 ? slideCount : null,
      completed: 0,
      sourceRef: { type: 'contentItem', id },
      deeplink: `/app/post/${encodeURIComponent(id)}/edit?tab=media`,
      contentId: id,
      expectedDurationMs: Math.max(20_000, slideCount * 10_000),
    });
    try {
      const body = { ...generationLevelPayloadFields(generationLevel, generationLevelPickerVisible) };
      const trimmedNotes = regenImageNotes.trim();
      if (trimmedNotes) body.instructions = trimmedNotes;
      if (regenImageLanguage) body.language = regenImageLanguage;
      if (singleScope && targetIndex >= 0 && targetIndex < carousel.length) {
        body.slideIndex = targetIndex + 1; // slide_index in D1 is 1-based.
      } else if (regenImageScope === 'all' && carousel.length > 0) {
        // Pass the existing slide count so the backend produces EXACTLY that
        // many slides (else it falls back to the IMAGE_POST_SLIDE_COUNT env).
        body.slideCount = carousel.length;
      }
      const res = await api.content.regenerateImage(id, body);
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      const afterSig = regenImageSignature(payload, watchIndex);
      if (payload && afterSig && afterSig !== beforeSig) {
        // Synchronous backend: the finished image is already in the response.
        applyRegenContent(payload);
        completeOperation(visualOpId, { status: 'succeeded', completed: slideCount > 1 ? slideCount : 1 });
        setRegenImageBusy(false);
        return;
      }
      // Async backend: image isn't in the response yet (generating server-side).
      // Poll for the swap — auto-updates the screen wherever the user is, and
      // the op keeps tracking even if they navigate away.
      pollForRegeneratedImage(visualOpId, beforeSig, watchIndex, slideCount);
    } catch (err) {
      failOperation(visualOpId, errMessage(err) || t('editPost.regenerateImageFailed'));
      if (err?.code === 'PLAN_LAPSED') {
        dash.invalidateResource?.('entitlements', { fresh: true });
        openUpgrade({ feature: 'default', variant: 'renew' });
      }
      setRegenImageBusy(false);
      console.error('Regenerate image error:', err);
      notify({ level: 'error', title: t('editPost.regenerateImageFailedAlert', { error: friendlyErrorMessage(err) }) });
    }
  };

  // Persist current slide order to backend. Called after every reorder/
  // move gesture so the user doesn't lose their work — no Save button
  // needed on the media tab. Debounce-style guard: skip if no slide has
  // a real id yet (post still in initial gen) or the order matches what
  // raw.carouselSlides already has.
  const persistSlideOrder = async (orderedSlides) => {
    if (!id || !orderedSlides?.length) return;
    const carousel = Array.isArray(raw?.carouselSlides) ? raw.carouselSlides : [];
    if (!carousel.length) return; // No persisted slides to reorder
    const newIds = orderedSlides.map((s) => s.id).filter((sid) => sid && !String(sid).startsWith('s'));
    if (newIds.length !== carousel.length) return; // Mismatch — skip
    // Skip if order is already canonical (don't spam the API on no-op moves).
    const currentIds = carousel
      .slice()
      .sort((a, b) => Number(a.slideIndex || 0) - Number(b.slideIndex || 0))
      .map((s) => s.id);
    if (currentIds.every((cid, i) => cid === newIds[i])) return;
    try {
      await api.content.reorderSlides(id, newIds);
      // Update the local raw.carouselSlides to reflect the new order so
      // subsequent persists compute the right diff.
      setRaw((prev) => prev ? { ...prev, carouselSlides: newIds.map((nid, i) => {
        const existing = (prev.carouselSlides || []).find((cs) => cs.id === nid);
        return existing ? { ...existing, slideIndex: i + 1 } : null;
      }).filter(Boolean) } : prev);
      setReorderSavedAt(Date.now());
    } catch (err) {
      console.error('Reorder save failed:', err);
      // Soft-fail: the user sees the slides in the new order locally;
      // we don't roll back. Next refresh will pull canonical state.
    }
  };

  // Delete a single carousel slide. Two-step: first click arms the
  // confirm state (small inline "Delete?" prompt on the slide), second
  // click within 4s actually fires the API. Auto-cancels if the user
  // doesn't follow through — no accidental deletion.
  const armDeleteSlide = (slideId) => {
    setConfirmingDeleteSlideId(slideId);
    // Auto-clear the armed state after 4s so a stale "Delete?" prompt
    // doesn't sit there forever if the user wanders off.
    setTimeout(() => {
      setConfirmingDeleteSlideId((current) => current === slideId ? null : current);
    }, 4000);
  };

  const cancelDeleteSlide = () => setConfirmingDeleteSlideId(null);

  const confirmDeleteSlide = async (slideId) => {
    if (!id || !slideId) return;
    setDeletingSlideId(slideId);
    try {
      await api.content.deleteSlide(id, slideId);
      // Refresh post state from server so slide list + indices are
      // canonical (backend re-packs slide_index after delete).
      const res = await api.content.get(id, { targetId: targetIdHint, fresh: true });
      const payload = res?.data && typeof res.data === 'object' ? res.data : res;
      if (payload) {
        setRaw(payload);
        const slidesRaw = payload?.carouselSlides || [];
        if (slidesRaw.length > 0) {
          setSlides(slidesRaw.map((s, i) => ({
            id: s.id || `s${i}`,
            kind: 'image',
            cap: s.caption || '',
            ...slideVideoFields(s.mediaAsset),
            imageUrl: isVideoAsset(s.mediaAsset) ? null : (s.mediaAsset?.publicUrl || s.imageUrl || null),
            grad: ['#888', '#444'],
            serif: false,
            tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
          })));
        }
        // Keep selection in-bounds.
        setSlideIdx((current) => Math.min(current, Math.max(0, slidesRaw.length - 1)));
      }
      setActivity({ kind: 'done', message: t('editPost.slideDeleted') });
    } catch (err) {
      setActivity(null);
      console.error('Delete slide error:', err);
      notify({ level: 'error', title: t('editPost.couldNotDeleteSlide', { error: friendlyErrorMessage(err) }) });
    } finally {
      setDeletingSlideId(null);
      setConfirmingDeleteSlideId(null);
    }
  };

  if (loading) {
    return (
      <div className="ep-root">
        <div className="ep-top">
          <div>
            <Link to={id ? `/app/post/${encodeURIComponent(id)}` : '/app/library'} className="ep-back">
              <I.chevLeft size={14} /> {t('editPost.back')}
            </Link>
            <h1 className="ep-h">{t('editPost.loading')}</h1>
          </div>
        </div>
      </div>
    );
  }
  if (error || !raw) {
    return (
      <div className="ep-root">
        <div className="ep-top">
          <div>
            <Link to="/app/library" className="ep-back"><I.chevLeft size={14} /> {t('editPost.library')}</Link>
            <h1 className="ep-h">{t('editPost.postNotFound')}</h1>
            <p className="ep-sub">{error || t('editPost.postNotFoundDetail')}</p>
          </div>
        </div>
      </div>
    );
  }

  const item = raw.contentItem;
  const itemStatus = item?.status || 'draft';

  return (
    <div className="ep-root">
      <CreationAccessNotice />
      <div className="ep-top">
        <div>
          <Link to={`/app/post/${encodeURIComponent(id)}`} className="ep-back">
            <I.chevLeft size={14} /> {t('editPost.postPreview')}
          </Link>
          <h1 className="ep-h">{t('editPost.title')}</h1>
          <p className="ep-sub">
            <span
              className={`ob-net-ic-${currentPlatform.id}`}
              style={{ width: 16, height: 16, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', verticalAlign: 'middle' }}
            >
              {BrandIcons[currentPlatform.id]?.({ size: 10 })}
            </span>
            <span style={{ verticalAlign: 'middle', marginLeft: 6 }}>
              {currentPlatform.name} · {itemStatus}
            </span>
          </p>
        </div>
        {isDirty && (
          <span className="pill pill-warning" style={{ alignSelf: 'center' }}>
            <span className="dot" />{t('editPost.unsavedChanges')}
          </span>
        )}
      </div>

      <div className="ep-grid">
        <div className="ep-form">
          <div className="ep-tabs">
            {[
              { id: 'copy', label: t('editPost.tabText'), icon: I.edit },
              { id: 'media', label: t('editPost.tabMedia'), icon: I.image, count: slides.length },
              { id: 'schedule', label: t('editPost.tabSchedule'), icon: I.calendar },
              { id: 'advanced', label: t('editPost.tabAdvanced'), icon: I.settings },
            ].map((tab) => (
              <button
                key={tab.id}
                className={`ep-tab ${activeTab === tab.id ? 'is-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                type="button"
              >
                <tab.icon size={13} />
                {tab.label}
                {tab.count != null && <span className="ep-tab-c mono">{tab.count}</span>}
              </button>
            ))}
          </div>

          {activeTab === 'copy' && (
            <section className="card ep-card">
              <EpField
                label={t('editPost.captionLabel')}
                count={caption.length}
                max={charLimit}
                // Hint kept short — the count badge in the field header
                // already shows the current platform's char limit, so a
                // redundant "Most restrictive limit shown (Instagram)"
                // sentence was noise. Per-account language mismatch is
                // surfaced in the Schedule tab where the user actually
                // picks accounts; no need to repeat it here.
                hint={t('editPost.captionHint', { platform: currentPlatform.name })}
              >
                <textarea
                  className="ep-textarea"
                  rows={8}
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder={t('editPost.captionPlaceholder')}
                />
              </EpField>

              {/* Optional "what to change" textarea — empty = generic
                  "improve clarity" pass; non-empty = passed as user
                  instructions to the regen LLM. Helps users steer the
                  rewrite ("make it shorter", "add a question", "less
                  salesy", etc) without writing a full new caption. */}
              <EpField
                label={t('editPost.whatToChangeLabel')}
                hint={t('editPost.whatToChangeHint')}
              >
                <textarea
                  className="ep-textarea"
                  rows={2}
                  value={regenNotes}
                  onChange={(e) => setRegenNotes(e.target.value)}
                  placeholder={t('editPost.whatToChangePlaceholder')}
                  disabled={regenTextBusy}
                />
              </EpField>

              {generationLevelPickerVisible && entitlementsChecked && !aiFullyUnavailable && (
                <GenerationLevelPicker
                  value={generationLevel}
                  onChange={setGenerationLevel}
                  operation="text"
                  disabled={regenTextBusy}
                  compact
                />
              )}

              {/* Compact inline language pill — mirrors the Media tab's
                  regen UX. Empty value = keep the post's existing
                  language; pick an ISO code to have the AI rewrite in
                  that language. Reuses the same .ep-regen-lang styles
                  so the picker visually matches across tabs. */}
              <div className="ep-regen-modifiers">
                <label className="ep-regen-lang">
                  <I.globe size={11} aria-hidden="true" />
                  <span className="ep-regen-lang-label">{t('editPost.languageLabel')}</span>
                  <select
                    value={regenTextLanguage}
                    onChange={(e) => setRegenTextLanguage(e.target.value)}
                    aria-label={t('editPost.textLanguageOverrideAria')}
                    className="ep-regen-lang-select"
                    disabled={regenTextBusy}
                  >
                    <option value="">{t('editPost.sameAsPost')}</option>
                    <option value="en">English</option>
                    <option value="uk">Українська</option>
                    <option value="ru">Русский</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="it">Italiano</option>
                    <option value="pl">Polski</option>
                    <option value="cs">Čeština</option>
                    <option value="pt">Português</option>
                    <option value="nl">Nederlands</option>
                    <option value="sv">Svenska</option>
                    <option value="lt">Lietuvių</option>
                    <option value="lv">Latviešu</option>
                  </select>
                </label>
              </div>

              <div className="ep-toolbar">
                <button
                  className="ep-toolbar-btn"
                  type="button"
                  onClick={handleRegenerate}
                  disabled={regenTextBusy || textGenerationDisabled}
                >
                  <I.sparkles size={12} />
                  {regenTextBusy ? t('editPost.regeneratingTextBusy') : t('editPost.regenerateText')}
                </button>
              </div>

              <EpField
                label={t('editPost.hashtagsLabel')}
                count={hashtags.length}
                max={30}
                hint={t('editPost.hashtagsHint')}
              >
                <input
                  className="ep-input"
                  type="text"
                  value={hashtagsInput}
                  onChange={(e) => setHashtagsInput(e.target.value)}
                  placeholder={t('editPost.hashtagsPlaceholder')}
                />
                {hashtags.length > 0 && (
                  <div className="ep-hashtags">
                    {hashtags.map((t, i) => (
                      <span key={i} className="ep-hashtag">{t}</span>
                    ))}
                  </div>
                )}
              </EpField>
              {/* Alt text removed per user feedback — it rarely matched
                  the actual image, added noise to the form, and the
                  per-platform alt is now set on publish by the platform
                  worker (Instagram/Twitter alt_text API). */}
            </section>
          )}

          {activeTab === 'media' && (
            <section className="card ep-card">
              <EpField label={t('editPost.slidesLabel')} hint={t('editPost.slidesHint')}>
                <div className="ep-slides">
                  {slides.map((s, i) => {
                    // Delete is hidden when the post is image-required
                    // and only 1 slide remains — backend would refuse.
                    // For other formats (text_post etc.) deletion is
                    // always available since the post can exist with
                    // zero slides.
                    const imageRequired = raw?.contentItem?.format === 'image_post'
                      || raw?.contentItem?.format === 'carousel';
                    const canDelete = !imageRequired || slides.length > 1;
                    return (
                      <SlideThumb
                        key={s.id}
                        slide={s}
                        idx={i}
                        total={slides.length}
                        isActive={i === slideIdx}
                        isRegenerating={isSlideRegenerating(i)}
                        onClick={() => setSlideIdx(i)}
                        onMoveLeft={() => moveSlide(i, -1)}
                        onMoveRight={() => moveSlide(i, +1)}
                        onReorder={reorderSlide}
                        canDelete={canDelete}
                        isConfirmingDelete={confirmingDeleteSlideId === s.id}
                        isDeleting={deletingSlideId === s.id}
                        onArmDelete={() => armDeleteSlide(s.id)}
                        onCancelDelete={cancelDeleteSlide}
                        onConfirmDelete={() => confirmDeleteSlide(s.id)}
                      />
                    );
                  })}
                </div>
              </EpField>

              {/* Inline regen controls. The textarea is the primary action —
                  always visible so users don't need an extra click. Scope
                  (when carousel >1 slide) and language sit below as
                  compact inline modifiers, matching the rest of the
                  form's field rhythm instead of a nested mini-card. */}
              <EpField
                label={t('editPost.whatToChangeImageLabel')}
                hint={t('editPost.whatToChangeImageHint')}
              >
                <textarea
                  className="ep-textarea"
                  rows={2}
                  value={regenImageNotes}
                  onChange={(e) => setRegenImageNotes(e.target.value)}
                  placeholder={t('editPost.whatToChangeImagePlaceholder')}
                  disabled={regenActive}
                />
              </EpField>

              {generationLevelPickerVisible && entitlementsChecked && !aiFullyUnavailable && (
                <GenerationLevelPicker
                  value={generationLevel}
                  onChange={setGenerationLevel}
                  operation="image"
                  disabled={regenActive}
                  compact
                />
              )}

              <div className="ep-regen-modifiers">
                {slides.length > 1 && (
                  <div className="ep-seg" role="radiogroup" aria-label={t('editPost.regenScopeAria')}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={regenImageScope === 'single'}
                      className={`ep-seg-btn ${regenImageScope === 'single' ? 'is-active' : ''}`}
                      onClick={() => setRegenImageScope('single')}
                      disabled={regenActive}
                    >
                      {t('editPost.justSlide', { number: slideIdx + 1 })}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={regenImageScope === 'all'}
                      className={`ep-seg-btn ${regenImageScope === 'all' ? 'is-active' : ''}`}
                      onClick={() => setRegenImageScope('all')}
                      disabled={regenActive}
                    >
                      {t('editPost.allSlides', { count: slides.length })}
                    </button>
                  </div>
                )}
                <label className="ep-regen-lang">
                  <I.globe size={11} aria-hidden="true" />
                  <span className="ep-regen-lang-label">{t('editPost.imageTextLabel')}</span>
                  <select
                    value={regenImageLanguage}
                    onChange={(e) => setRegenImageLanguage(e.target.value)}
                    aria-label={t('editPost.imageTextLanguageOverrideAria')}
                    className="ep-regen-lang-select"
                    disabled={regenActive}
                  >
                    <option value="">{t('editPost.sameAsPost')}</option>
                    <option value="en">English</option>
                    <option value="uk">Українська</option>
                    <option value="ru">Русский</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                    <option value="es">Español</option>
                    <option value="it">Italiano</option>
                    <option value="pl">Polski</option>
                    <option value="cs">Čeština</option>
                    <option value="pt">Português</option>
                    <option value="nl">Nederlands</option>
                    <option value="sv">Svenska</option>
                    <option value="lt">Lietuvių</option>
                    <option value="lv">Latviešu</option>
                  </select>
                </label>
              </div>

              <div className="ep-media-tools" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* Distinct from copy-tab Regenerate: this only re-generates
                    the images, keeps caption/copy as-is. Hits the
                    /regenerate-image endpoint added in content-worker. */}
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={handleRegenerateImage}
                  disabled={regenActive || uploadImageBusy || !opsChecked || imageGenerationDisabled}
                  aria-describedby={imageGenerationDisabled ? 'edit-image-availability' : undefined}
                >
                  <I.sparkles size={12} />
                  {regenActive ? t('editPost.regeneratingImageBusy') : t('imageLocked.regenerate')}
                </button>
                <label className={`btn btn-ghost btn-sm ${uploadImageBusy || regenActive ? 'is-disabled' : ''}`} style={{ cursor: uploadImageBusy || regenActive ? 'not-allowed' : 'pointer', opacity: uploadImageBusy || regenActive ? 0.55 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <I.image size={12} />
                  {uploadImageBusy ? t('editPost.uploading') : imageAllowance.zero ? t('imageLocked.upload') : t('editPost.uploadCustomImage')}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploadImageBusy || regenActive || !creationAllowed}
                    style={{ display: 'none' }}
                    onChange={handleUploadCustomImage}
                  />
                </label>
                {/* TASK-052: the other half of "add an image" — reuse something
                    already in the Media library instead of re-uploading it. */}
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={() => setLibraryPickerOpen(true)}
                  disabled={regenActive || uploadImageBusy || attachMediaBusy || !creationAllowed}
                >
                  <I.image size={12} />
                  {attachMediaBusy ? t('editPost.attachingMedia') : t('editPost.attachFromLibrary')}
                </button>
              </div>
              {imageGenerationDisabled && (
                <p id="edit-image-availability" className="muted" role="status" style={{ margin: '8px 0 0', fontSize: 13 }}>
                  {imageAllowance.zero
                    ? t('imageLocked.lockedLabel')
                    : imageAvailabilityError
                      ? t('imageLocked.error')
                      : t('imageLocked.loading')}
                </p>
              )}
            </section>
          )}

          {activeTab === 'schedule' && (
            <section className="card ep-card">
              <EpField label={t('editPost.whenToPublish')}>
                <div className="ep-radio-group">
                  <button
                    className={`ep-radio ${scheduleMode === 'now' ? 'is-active' : ''}`}
                    onClick={() => setScheduleMode('now')}
                    type="button"
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">{t('editPost.publishNow')}</div>
                      <div className="ep-radio-s">{t('editPost.publishNowDesc')}</div>
                    </div>
                  </button>
                  <button
                    className={`ep-radio ${scheduleMode === 'scheduled' ? 'is-active' : ''}`}
                    onClick={() => setScheduleMode('scheduled')}
                    type="button"
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">{t('editPost.schedule')}</div>
                      <div className="ep-radio-s">{t('editPost.scheduleDesc')}</div>
                    </div>
                  </button>
                  <button
                    className={`ep-radio ${scheduleMode === 'draft' ? 'is-active' : ''} ${isDraft ? 'is-disabled' : ''}`}
                    onClick={() => {
                      if (isDraft) return; // already a draft — no-op
                      setScheduleMode('draft');
                    }}
                    disabled={isDraft}
                    type="button"
                    style={isDraft ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  >
                    <span className="ep-radio-dot" />
                    <div>
                      <div className="ep-radio-l">
                        {isDraft ? t('editPost.alreadyADraft') : t('editPost.saveAsDraft')}
                      </div>
                      <div className="ep-radio-s">
                        {isDraft
                          ? t('editPost.alreadyADraftDesc')
                          : t('editPost.saveAsDraftDesc')}
                      </div>
                    </div>
                  </button>
                </div>
              </EpField>

              {scheduleMode === 'scheduled' && (
                <EpField label={t('editPost.dateTimeLabel')} hint={t('editPost.dateTimeHint')}>
                  {/* Quick-pick chips. Each one writes a value in the local
                      datetime-local format ("YYYY-MM-DDTHH:mm") so the
                      input below picks it up and the user can still
                      fine-tune. Removes the awkward "click 5 times in
                      a native picker" experience for the common cases. */}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
                    {[
                      { id: 'in1hour', label: t('editPost.quickIn1Hour'), mins: 60 },
                      { id: 'tomorrow9am', label: t('editPost.quickTomorrow9am'), mins: null, hour: 9, dayOffset: 1 },
                      { id: 'tomorrow6pm', label: t('editPost.quickTomorrow6pm'), mins: null, hour: 18, dayOffset: 1 },
                      { id: 'nextMonday9am', label: t('editPost.quickNextMonday9am'), mins: null, hour: 9, nextDay: 1 },
                      { id: 'in1week', label: t('editPost.quickIn1Week'), mins: 60 * 24 * 7 },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => {
                          const d = new Date();
                          if (opt.mins) {
                            d.setMinutes(d.getMinutes() + opt.mins);
                          } else if (opt.dayOffset != null) {
                            d.setDate(d.getDate() + opt.dayOffset);
                            d.setHours(opt.hour, 0, 0, 0);
                          } else if (opt.nextDay != null) {
                            const delta = (opt.nextDay - d.getDay() + 7) % 7 || 7;
                            d.setDate(d.getDate() + delta);
                            d.setHours(opt.hour, 0, 0, 0);
                          }
                          // Format as YYYY-MM-DDTHH:mm in LOCAL time — the
                          // datetime-local input expects local, not UTC.
                          const pad = (n) => String(n).padStart(2, '0');
                          const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                          setScheduledAt(local);
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input
                    className="ep-input"
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                  />
                  <DeviceTimezoneLabel />
                  {/* A past time needs a new choice; it never means publish now. */}
                  {scheduledAt && (() => {
                    const ts = new Date(scheduledAt).getTime();
                    if (Number.isFinite(ts) && ts <= Date.now()) {
                      return (
                        <div style={{
                          fontSize: 12,
                          color: 'var(--accent)',
                          marginTop: 6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                        }}>
                          <I.alert size={12} /> {t('editPost.timePassedHint')}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </EpField>
              )}

              <EpField label={t('editPost.publishToLabel')} hint={t('editPost.publishToHint')}>
                {selectedTargetMissing && <p className="ep-recovery-note" role="alert">{t('editPost.selectedPublicationUnavailable')}</p>}
                {recoverySource && scheduleMode !== 'draft' && (
                  <div className="ep-recovery-note" role="note">
                    <strong>{t('editPost.recoveryTitle')}</strong>
                    <span>{recoveryCandidates.length
                      ? t('editPost.recoveryExistingPublication')
                      : t('editPost.recoverySameAccountOnly')}</span>
                    {recoveryCandidates.length === 0 && (
                      <label className="ep-recovery-confirm">
                        <input type="checkbox" checked={confirmedUnpublished}
                          onChange={(event) => setConfirmedUnpublished(event.target.checked)} />
                        <span>{t('editPost.confirmUnpublishedLabel')}</span>
                      </label>
                    )}
                  </div>
                )}
                {alreadyPublishedIds.size > 0 && <p className="ep-published-note">{t('editPost.publishedAccountsHint')}</p>}
                <div className="ep-plats">
                  {/* Render one row per connected account so multi-account
                      workspaces (e.g. two Telegrams) can be picked
                      individually. Disabled rows show platforms with no
                      connected account so the user knows what's missing. */}
                  {accountsLoading ? (
                    <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
                      {t('editPost.loadingConnectedAccounts')}
                    </div>
                  ) : allAccounts.length === 0 ? (
                    <div className="muted" style={{ fontSize: 13, padding: '8px 0', lineHeight: 1.5 }}>
                      {t('editPost.noConnectedAccounts')}{' '}
                      <Link to="/app/settings?tab=accounts" style={{ color: 'var(--accent)', fontWeight: 600 }}>
                        {t('editPost.connectInSettings')}
                      </Link>
                    </div>
                  ) : null}
                  {allAccounts.map((acc) => {
                    const p = PLATFORM_DEFS.find((x) => x.id === acc.platform);
                    if (!p) return null;
                    const on = selectedAccounts.has(acc.id);
                    const wasPublished = alreadyPublishedIds.has(acc.id);
                    const langChip = acc.publishLanguage || acc.defaultPublishLanguage;
                    // Language-mismatch notice: shown only when this account
                    // is SELECTED for publishing AND its publishLanguage is
                    // set AND it differs from the language the post was
                    // generated in. We don't surface it for un-selected
                    // rows — that would clutter the picker without value
                    // (the user might just be browsing before picking).
                    const accLang = (langChip || '').toLowerCase();
                    const postLangNorm = (raw?.job?.language || '').toLowerCase();
                    const langMismatch = on && accLang && postLangNorm && accLang !== postLangNorm;
                    return (
                      <div key={acc.id} className="ep-plat-row">
                        <button
                          className={`ep-plat ${on ? 'is-on' : ''}`}
                          aria-pressed={on}
                          onClick={() => toggleAccount(acc.id)}
                          type="button"
                        >
                          <span
                            className={`ob-net-ic-${p.id}`}
                            style={{ width: 26, height: 26, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                          >
                            {BrandIcons[p.id]?.({ size: 13 })}
                          </span>
                          <div className="ep-plat-text">
                            <div className="ep-plat-name" style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span>{p.name}</span>
                              {wasPublished && <span className="ep-published-badge">{t('editPost.alreadyPublishedAccount')}</span>}
                              {langChip && (
                                <span style={{
                                  fontSize: 9,
                                  fontFamily: 'var(--font-mono)',
                                  letterSpacing: '0.04em',
                                  padding: '1px 5px',
                                  background: 'var(--accent-soft)',
                                  color: 'var(--accent)',
                                  borderRadius: 3,
                                  border: '1px solid var(--border-2)',
                                }}>
                                  🌐 {String(langChip).toUpperCase()}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                              {acc.displayName || acc.externalAccountId || t('editPost.connected')}
                            </div>
                          </div>
                          <span className={`ep-plat-toggle ${on ? 'is-on' : ''}`}>
                            {on && <I.check size={11} />}
                          </span>
                        </button>
                        {wasPublished && on && <div className="ep-repeat-note" role="note">{t('editPost.selectedRepeatHint')}</div>}
                        {langMismatch && (
                          <div className="ep-plat-lang-warn" role="note">
                            <I.info size={11} aria-hidden="true" />
                            <span className="ep-plat-lang-warn-text">
                              {t('editPost.langMismatchNotice', {
                                postLang: languageLabel(postLangNorm),
                                accountLang: languageLabel(accLang),
                              })}
                            </span>
                            <Link
                              to="/app/settings?tab=social"
                              className="ep-plat-lang-warn-link"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {t('editPost.changeLink')}
                            </Link>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {/* TikTok is publish-gated (no Content Posting audit yet) and
                      can't be connected in Settings, so it never appears as a
                      connected account above. Surface it as a disabled
                      "coming soon" row — like the New Post picker — so an
                      already-generated post can't be targeted at TikTok and the
                      user sees it's a known-but-upcoming channel, not missing. */}
                  {!allAccounts.some((a) => a.platform === 'tiktok') && (
                    <div className="ep-plat-row">
                      <button
                        className="ep-plat is-disabled"
                        type="button"
                        onClick={() => {}}
                        disabled
                        title={t('createPost.tiktokComingSoon')}
                        style={{ opacity: 0.55, cursor: 'not-allowed' }}
                      >
                        <span
                          className="ob-net-ic-tiktok"
                          style={{ width: 26, height: 26, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                        >
                          {BrandIcons.tiktok?.({ size: 13 })}
                        </span>
                        <div className="ep-plat-text">
                          <div className="ep-plat-name"><span>TikTok</span></div>
                          <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>
                            {t('createPost.comingSoon')}
                          </div>
                        </div>
                        <span className="ep-plat-toggle" />
                      </button>
                    </div>
                  )}
                </div>
              </EpField>
            </section>
          )}

          {activeTab === 'advanced' && (
            <section className="card ep-card">
              <div className="ep-danger">
                <div>
                  <div className="ep-danger-h">{t('editPost.deleteThisPost')}</div>
                  <div className="ep-danger-s">{t('editPost.deleteThisPostDesc')}</div>
                </div>
                <button className="btn btn-sm pp-danger" onClick={handleDelete} type="button">
                  <I.trash size={12} /> {t('editPost.deletePost')}
                </button>
              </div>
            </section>
          )}
        </div>

        <aside className="ep-side">
          <div className="ep-side-sticky">
            <div className="ep-side-h">
              <div className="eyebrow"><span>{t('editPost.livePreview')}</span></div>
              <h3>{t('editPost.howItLooks')}</h3>
            </div>
            <MiniPreview
              platform={currentPlatform}
              caption={caption}
              hashtags={hashtags}
              slide={slides[slideIdx] || slides[0]}
              isRegenerating={isSlideRegenerating(slideIdx)}
              slideIdx={slideIdx}
              slideCount={slides.length}
              handle={handle}
              onPrev={() => setSlideIdx((current) => Math.max(0, current - 1))}
              onNext={() => setSlideIdx((current) => Math.min(slides.length - 1, current + 1))}
              onOpen={() => setLightboxOpen(true)}
            />

            <div className="ep-actions card">
              {scheduleMode === 'now' ? (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving || accountsLoading || !creationAllowed || selectedTargetMissing}
                  type="button"
                >
                  <I.zap size={13} /> {t('editPost.savePublishNow')}
                </button>
              ) : scheduleMode === 'draft' ? (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving || !canSaveDraftAction || !creationAllowed}
                  type="button"
                >
                  <I.check size={13} />
                  {' '}
                  {/* Enabled only via unscheduling (no text edits) — the
                      button re-uses the radio's own "Save as draft" copy so
                      it reads as the same action the user just picked,
                      instead of "Save draft" implying a fresh text save. */}
                  {!isDirty && canSaveDraftAction ? t('editPost.saveAsDraft') : t('editPost.saveDraft')}
                </button>
              ) : (
                <button
                  className="btn btn-primary ep-cta"
                  onClick={handleSave}
                  disabled={saving || accountsLoading || !creationAllowed || selectedTargetMissing}
                  type="button"
                >
                  <I.check size={13} /> {scheduledAt ? t('editPost.savePublishAt', { when: fmtScheduled(scheduledAt) }) : t('editPost.choosePublishTime')}
                </button>
              )}
              <div className="ep-actions-row">
                <Link className="btn btn-ghost btn-sm" to={`/app/post/${encodeURIComponent(id)}`}>{t('editPost.cancel')}</Link>
                <Link className="btn btn-ghost btn-sm" to={`/app/post/${encodeURIComponent(id)}`}>
                  <I.eye size={12} /> {t('editPost.preview')}
                </Link>
              </div>
            </div>
          </div>
        </aside>
      </div>
      {lightboxOpen && slides.length > 0 && (
        <SlideLightbox
          slides={slides}
          slideIdx={slideIdx}
          onClose={() => setLightboxOpen(false)}
          onPrev={() => setSlideIdx((current) => Math.max(0, current - 1))}
          onNext={() => setSlideIdx((current) => Math.min(slides.length - 1, current + 1))}
        />
      )}
      <ConfirmDialog
        open={Boolean(republishConfirmation)}
        tone="warn"
        title={t('editPost.repeatPublishTitle')}
        description={t('editPost.repeatPublishDescription')}
        confirmLabel={scheduleMode === 'now' ? t('editPost.publishAgainNow') : t('editPost.scheduleAgain')}
        cancelLabel={t('editPost.cancel')}
        confirming={saving}
        onConfirm={() => handleSave({ republishAccountIds: republishConfirmation?.accountIds || [] })}
        onCancel={() => setRepublishConfirmation(null)}
      >
        <ul className="ep-repeat-accounts">
          {(republishConfirmation?.accountIds || []).map((accountId) => {
            const account = allAccounts.find((a) => a.id === accountId);
            const platform = PLATFORM_DEFS.find((p) => p.id === account?.platform)?.name;
            return <li key={accountId}>{platform} · {account?.displayName || account?.externalAccountId || accountId}</li>;
          })}
        </ul>
        <p className="ep-repeat-time">{scheduleMode === 'now' ? t('editPost.publishNow')
          : `${fmtScheduled(scheduledAt)} · ${Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'}`}</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirmDeleteOpen}
        tone="danger"
        title={t('editPost.deletePost')}
        description={t('editPost.deletePostConfirm')}
        confirmLabel={t('editPost.deletePost')}
        cancelLabel={t('editPost.cancel')}
        confirming={deleting}
        onConfirm={performDelete}
        onCancel={() => setConfirmDeleteOpen(false)}
      />
      {/* TASK-052: same picker the New Post screen and Brand Kit use, so the
          library behaves identically wherever the user meets it. */}
      <LibraryPicker
        open={libraryPickerOpen}
        max={10}
        title={t('editPost.attachFromLibraryTitle')}
        confirmLabel={t('editPost.attachFromLibraryConfirm')}
        onClose={() => setLibraryPickerOpen(false)}
        onConfirm={handleAttachFromLibrary}
      />
    </div>
  );
};

export default EditPost;
